import { useAuthStore } from "../stores/authStore";
import axios from "axios";

export const https = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

declare module "axios" {
  export interface AxiosRequestConfig {
    _retry?: boolean;
    skipAuthRefresh?: boolean;
  }
}

function getErrorMessage(err: unknown) {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as any;
    const firstString = (...candidates: unknown[]) =>
      candidates.find((c) => typeof c === "string" && c.trim()) as
        | string
        | undefined;

    const message = firstString(
      data?.message,
      data?.message?.error,
      data?.message?.messages?.[0],
    );

    const errorFromArray = firstString(
      data?.error?.[0]?.message,
      data?.error?.[0]?.messages?.[0],
    );

    return (
      message ||
      errorFromArray ||
      (typeof data?.error === "string" ? data.error : undefined) ||
      err.response?.statusText ||
      err.message ||
      "Something went wrong"
    );
  }
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "Something went wrong";
}

https.interceptors.request.use((config) => {
  const accessToken = useAuthStore.getState().accessToken;

  if (accessToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

https.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    let respError = error;
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh
    ) {
      const { accessToken, refreshAuthToken, logout } = useAuthStore.getState();
      if (!accessToken) {
        logout();
      } else {
        originalRequest._retry = true;
        try {
          await refreshAuthToken();
          return https(originalRequest);
        } catch (refreshErr) {
          logout();
          respError = refreshErr;
        }
      }
    }

    return Promise.reject({
      ...respError,
      message: getErrorMessage(respError),
    });
  },
);
